/**
 * Chart geometry.
 *
 * `splinePath` is the design's own cardinal-spline builder, ported unchanged —
 * the soft curve is what distinguishes these charts from a plain polyline.
 * Everything else exists because the mock drew 60 synthetic points while the
 * live console buffers up to an hour of real ones.
 */

export interface Point {
  timestamp: number;
  value: number;
}

/**
 * Cardinal spline through `data`, scaled into a `w`×`h` box. `max` fixes the
 * ceiling; omitting it fits the series with 15% headroom. `close` returns the
 * area variant, dropped to the baseline and closed for a gradient fill.
 */
export function splinePath(
  data: readonly number[],
  w: number,
  h: number,
  max?: number,
  close = false,
): string {
  if (data.length < 2) return '';
  const top = max || Math.max(...data, 1) * 1.15;
  const pts = data.map((v, i): [number, number] => [
    (i / (data.length - 1)) * w,
    h - Math.min(1, Math.max(0, v / top)) * h,
  ]);

  const first = pts[0]!;
  let d = `M ${first[0].toFixed(1)} ${first[1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p0 = pts[i - 1] ?? p1;
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return close ? `${d} L ${w} ${h} L 0 ${h} Z` : d;
}

/**
 * Average `data` down to at most `target` buckets. An hour of one-second
 * samples is 3600 points; a spline through all of them is a ~250 KB path
 * string that no browser needs to rasterise for a 1000px-wide chart.
 */
export function downsample(data: readonly number[], target: number): number[] {
  if (data.length <= target) return [...data];
  const out: number[] = [];
  const size = data.length / target;
  for (let i = 0; i < target; i += 1) {
    const start = Math.floor(i * size);
    const end = Math.max(start + 1, Math.floor((i + 1) * size));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += data[j] ?? 0;
    out.push(sum / (end - start));
  }
  return out;
}

/**
 * Pick a stable ceiling for an axis.
 *
 * Charts that rescale on every frame make a steady series look like it is
 * moving, so the ceiling only ever ratchets up: the caller passes the previous
 * ceiling and gets back the same value unless the data has outgrown it.
 */
export function ceilingFor(
  peak: number,
  steps: readonly number[],
  previous: number,
  hardMax?: number,
): number {
  // Step up while the peak still has 15% of the axis above it, so a series
  // running near its ceiling does not flatten into the top gridline.
  const wanted = steps.find((step) => peak <= step * 0.85) ?? steps[steps.length - 1]!;
  const next = Math.max(previous, wanted);
  return hardMax ? Math.min(next, Math.max(hardMax, steps[0]!)) : next;
}

/** CPU is reported per core, so a 4-core host can legitimately reach 400%. */
export const CPU_STEPS = [100, 200, 400, 800, 1600, 3200] as const;

/** Memory ceilings in MiB. */
export const MEMORY_STEPS = [256, 512, 1024, 2048, 4096, 8192, 16384, 32768] as const;

/** Four evenly spaced gridlines plus the baseline, as in the design. */
export function axisLabels(ceiling: number, decimals = 0): string[] {
  return [1, 0.75, 0.5, 0.25, 0].map((f) => (ceiling * f).toFixed(decimals));
}
